import { useLayoutEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { EditorState, type Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { getJsonCodeEditorColors } from '../../theme/codeEditorColors';
import { buildJsonCodeMirrorExtensions } from './jsonCodeMirrorTheme';

export interface JsonEditorProps {
  /** Initial document — remount with React `key` when content should reset. */
  value: string;
  onChange?: (val: string) => void;
  readOnly?: boolean;
  minHeight?: number | string;
}

export function JsonEditor({
  value,
  onChange,
  readOnly = false,
  minHeight,
}: JsonEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const muiTheme = useTheme();
  const colors = useMemo(() => getJsonCodeEditorColors(muiTheme), [muiTheme]);

  onChangeRef.current = onChange;

  useLayoutEffect(() => {
    const parent = hostRef.current;
    if (!parent) return;

    const extensions: Extension[] = [
      ...buildJsonCodeMirrorExtensions(colors, readOnly),
      EditorView.updateListener.of((update) => {
        if (!update.docChanged) return;
        onChangeRef.current?.(update.state.doc.toString());
      }),
    ];

    if (readOnly) {
      extensions.push(EditorState.readOnly.of(true));
    }

    const view = new EditorView({
      state: EditorState.create({ doc: value, extensions }),
      parent,
    });

    return () => {
      view.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount via parent React key only
  }, []);

  return (
    <Box
      sx={{
        flex: minHeight === undefined ? 1 : undefined,
        width: '100%',
        minHeight: minHeight ?? 0,
        height: minHeight,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: colors.panelBg,
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        '& .cm-editor': {
          height: '100%',
          flex: 1,
          outline: 'none',
        },
        '& .cm-scroller': {
          overflow: 'auto',
        },
      }}
    >
      <div
        ref={hostRef}
        style={{
          width: '100%',
          height: '100%',
          flex: 1,
          minHeight: 0,
        }}
      />
    </Box>
  );
}
